import { AccountRepository, CreateAccountData } from '../../domain/ports/AccountRepository';
import { Account, createAccount } from '../../domain/models/Account';
import { AccountActionLink, AccountLinks } from '../../domain/models/AccountStatus';
import { HttpClient } from '../../../common/infrastructure/http/HttpClient';

/**
 * DTO interface for a single account in list API response
 */
interface AccountListItemDTO {
  id: string;
  name: string;
  status: string;
  links: AccountLinks;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * DTO interface for the account details API response
 * GET /admin/accounts/{id} returns name, status, timezone, created_at, modified_at
 */
interface AccountDetailsDTO {
  id: string;
  name: string;
  status: string;
  timezone: string;
  links?: AccountLinks;
  createdAt?: string;
  modifiedAt?: string;
  locale?: {
    region: string;
    timezone: string;
    language: string;
  };
  owner?: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
}

/**
 * DTO interface for account list API response
 * Following backend pattern: all responses wrapped in "data" property
 */
interface AccountListResponseDTO {
  data: AccountListItemDTO[];
}

/**
 * DTO interface for single account API response (details)
 * Following backend pattern: all responses wrapped in "data" property
 */
interface AccountDetailsResponseDTO {
  data: AccountDetailsDTO;
}

/**
 * DTO interface for single account API response (action results)
 * Following backend pattern: all responses wrapped in "data" property
 */
interface AccountResponseDTO {
  data: AccountListItemDTO;
}

/**
 * DTO interface for the account registration API response
 * POST /admin/accounts returns 202 with the new tenantId
 */
interface RegisterAccountResponseDTO {
  data: {
    tenantId: string;
  };
}

/**
 * HTTP implementation of AccountRepository
 * Communicates with the Symfony backend API
 */
export class HttpAccountRepository implements AccountRepository {
  constructor(private httpClient: HttpClient) {}

  /**
   * Retrieve all accounts
   */
  async getAll(): Promise<Account[]> {
    try {
      const response = await this.httpClient.get<AccountListResponseDTO>('/accounts');

      // Map DTOs to domain models
      return response.data.map((dto) =>
        createAccount({
          id: dto.id,
          name: dto.name,
          status: dto.status,
          links: dto.links,
          createdAt: dto.createdAt,
          updatedAt: dto.updatedAt,
        }),
      );
    } catch (error) {
      // Re-throw with additional context if needed
      if (error instanceof Error) {
        throw new Error(`Failed to fetch accounts: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Retrieve a single account by ID
   */
  async getById(id: string): Promise<Account> {
    try {
      const response = await this.httpClient.get<AccountDetailsResponseDTO>(`/accounts/${id}`);

      const dto = response.data;

      return createAccount({
        id: dto.id,
        name: dto.name,
        status: dto.status,
        timezone: dto.timezone,
        links: dto.links ?? { self: `/admin/accounts/${id}` },
        createdAt: dto.createdAt,
        updatedAt: dto.modifiedAt,
        locale: dto.locale,
        owner: dto.owner,
      });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch account: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Update basic data (name and timezone) for an existing account
   */
  async update(id: string, data: { name: string; timezone: string }): Promise<void> {
    try {
      await this.httpClient.put<void>(`/accounts/${id}`, data);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to update account: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Manually register a new business account.
   * Backend returns 202 with `{ data: { tenantId } }`.
   */
  async create(data: CreateAccountData): Promise<{ tenantId: string }> {
    try {
      const response = await this.httpClient.post<RegisterAccountResponseDTO>('/accounts', data);
      return { tenantId: response.data.tenantId };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to create account: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Delete an account permanently (hard delete)
   * Account must be in REMOVED status on the backend
   */
  async delete(id: string): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/accounts/${id}`);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to delete account: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Execute an account action using the backend-provided action link
   * @param actionLink HATEOAS action link from backend
   * @returns Updated account after action execution or null if no content returned
   */
  async executeAccountAction(actionLink: AccountActionLink): Promise<Account | null> {
    try {
      const method = actionLink.method.toLowerCase();
      let response: AccountResponseDTO | null;

      // Dynamic HTTP method dispatch based on backend link
      switch (method) {
        case 'get':
          response = await this.httpClient.get<AccountResponseDTO>(actionLink.href);
          break;
        case 'post':
          response = await this.httpClient.post<AccountResponseDTO>(actionLink.href);
          break;
        case 'put':
          response = await this.httpClient.put<AccountResponseDTO>(actionLink.href);
          break;
        case 'patch':
          response = await this.httpClient.patch<AccountResponseDTO>(actionLink.href);
          break;
        case 'delete':
          response = await this.httpClient.delete<AccountResponseDTO>(actionLink.href);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${actionLink.method}`);
      }

      // Handle 204 No Content or empty response
      if (!response) {
        return null;
      }

      // Map DTO to domain model
      return createAccount({
        id: response.data.id,
        name: response.data.name,
        status: response.data.status,
        links: response.data.links,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
      });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to execute action: ${error.message}`);
      }
      throw error;
    }
  }
}
